import React, { useState, useEffect, useRef, useCallback } from 'react';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
  useForm,
} from './Form';
import { Input } from './Input';
import { Button } from './Button';
import { Spinner } from './Spinner';
import { useAuth } from '../helpers/useAuth';
import {
  schema,
  postRegister,
  InputType,
} from '../endpoints/auth/register_with_password_POST.schema';
import styles from './PasswordRegisterForm.module.css';

const TURNSTILE_SITE_KEY = '0x4AAAAAAC8M8hdItfRhvkfq';

export type RegisterFormData = InputType;

interface PasswordRegisterFormProps {
  className?: string;
  defaultValues?: Partial<RegisterFormData>;
  redirectTo?: string;
}

export const PasswordRegisterForm: React.FC<PasswordRegisterFormProps> = ({
  className,
  defaultValues,
  redirectTo = '/',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const { onLogin } = useAuth();
  const navigate = useNavigate();

  // Load Cloudflare Turnstile script
  useEffect(() => {
    if (document.querySelector('script[src*="turnstile"]')) return;
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  // Render Turnstile widget
  const renderTurnstile = useCallback(() => {
    if (!turnstileRef.current || !(window as any).turnstile) return;
    // Clear previous widget
    turnstileRef.current.innerHTML = '';
    (window as any).turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: 'dark',
      callback: (token: string) => setTurnstileToken(token),
      'expired-callback': () => setTurnstileToken(null),
      'error-callback': () => setTurnstileToken(null),
    });
  }, []);

  useEffect(() => {
    // If turnstile already loaded, render immediately
    if ((window as any).turnstile) {
      renderTurnstile();
    } else {
      // Wait for script to load
      (window as any).onTurnstileLoad = renderTurnstile;
    }
  }, [renderTurnstile]);

  const form = useForm({
    schema,
    defaultValues: defaultValues || {
      email: '',
      password: '',
      displayName: '',
    },
  });

  const handleSubmit = async (data: z.infer<typeof schema>) => {
    setError(null);

    if (!turnstileToken) {
      setError('Please complete the verification challenge.');
      return;
    }

    setIsLoading(true);

    try {
      const result = await postRegister({ ...data, turnstileToken } as any);
      console.log('Registration successful for:', data.email);
      onLogin(result.user);
      navigate(redirectTo);
    } catch (err) {
      console.error('Registration error:', err);

      if (err instanceof Error) {
        const errorMessage = err.message;

        if (errorMessage.includes('Email already in use')) {
          setError('This email is already registered. Please try logging in instead.');
        } else if (errorMessage.toLowerCase().includes('display name')) {
          setError("Please provide a valid display name that isn't empty.");
        } else if (errorMessage.includes('display') || errorMessage.includes('name')) {
          setError('Please check your display name: ' + errorMessage);
        } else {
          setError(errorMessage || 'Registration failed. Please try again.');
        }
      } else {
        console.log('Unknown error type:', err);
        setError('Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      {error && <div className={styles.errorMessage}>{error}</div>}
      <form
        onSubmit={form.handleSubmit((data) => handleSubmit(data as z.infer<typeof schema>))}
        className={`${styles.form} ${className || ''}`}
      >
        <FormItem name="email">
          <FormLabel>Email</FormLabel>
          <FormControl>
            <Input
              placeholder="your@email.com"
              value={form.values.email || ''}
              onChange={(e) =>
                form.setValues((prev: any) => ({
                  ...prev,
                  email: e.target.value,
                }))
              }
            />
          </FormControl>
          <FormMessage />
        </FormItem>

        <FormItem name="displayName">
          <FormLabel>Display Name</FormLabel>
          <FormControl>
            <Input
              id="register-display-name"
              placeholder="Your Name"
              value={form.values.displayName || ''}
              onChange={(e) =>
                form.setValues((prev: any) => ({
                  ...prev,
                  displayName: e.target.value,
                }))
              }
            />
          </FormControl>
          <FormDescription>Spaces, emojis, and special characters are all allowed</FormDescription>
          <FormMessage />
        </FormItem>

        <FormItem name="password">
          <FormLabel>Password</FormLabel>
          <FormControl>
            <Input
              type="password"
              placeholder="••••••••"
              value={form.values.password || ''}
              onChange={(e) =>
                form.setValues((prev: any) => ({
                  ...prev,
                  password: e.target.value,
                }))
              }
            />
          </FormControl>
          <FormDescription>
            At least 8 characters with uppercase, lowercase, and number
          </FormDescription>
          <FormMessage />
        </FormItem>

        <div ref={turnstileRef} style={{ margin: '0.5rem 0' }} />

        <Button type="submit" disabled={isLoading || !turnstileToken} className={styles.submitButton}>
          {isLoading ? (
            <>
              <Spinner size="sm" /> Creating Account...
            </>
          ) : (
            'Create Account'
          )}
        </Button>
      </form>
    </Form>
  );
};
